import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Select, MenuItem, FormControl, InputLabel,
  Stack, CircularProgress, Alert, Paper, Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp as TrendIcon,
  TrendingDown as ExpenseIcon,
  AccountBalance as BankIcon,
  Engineering as TechIcon,
  Autorenew as RenewalIcon,
  Build as InstallIcon,
  Payments as CommissionIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const StatCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, subtitle, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, backgroundColor: `${color}15` }}>
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// 'YYYY-MM' → "agosto" (o "agosto 2025" si no es el año que se mira).
const nombreMes = (clave: string, anioVisto: number) => {
  if (clave === 'antes') return 'antes de que existiera el registro';
  const [a, m] = clave.split('-').map(Number);
  const nombre = MONTHS[m - 1]?.toLowerCase() || clave;
  return a === anioVisto ? nombre : `${nombre} ${a}`;
};

// "$12,500 de agosto" o, con `en`, "$12,500 en septiembre".
const listaMeses = (filas: { mes: string; monto: number }[], anio: number, fmt: (n: number) => string, conector = 'de') =>
  filas.map((f) => `${fmt(f.monto)} ${conector} ${nombreMes(f.mes, anio)}`).join(' y ');

/**
 * Cuándo lo que se movió en el mes no es lo que se generó en el mes.
 *
 * El reporte cuenta cada peso en el mes en que se genera; los pagos y entregas conservan su
 * fecha real. Si una liquidación cruza de mes —el 1-sep se pagaron comisiones de agosto—, las
 * cifras de arriba y los movimientos del técnico no coinciden, y sin esta explicación parece
 * un error. Solo aparece cuando hay algo que explicar.
 */
const ConciliacionCard: React.FC<{ c: any; mes: number; anio: number; fmt: (n: number) => string }> = ({ c, mes, anio, fmt }) => {
  if (!c) return null;
  const nombre = MONTHS[mes - 1].toLowerCase();
  const com = c.comisiones;
  const caja = c.efectivo;
  const lineas: { texto: React.ReactNode; aviso?: boolean }[] = [];

  if (com.pagadasDeOtrosMeses.length) {
    lineas.push({ texto: <>En {nombre} se <strong>pagaron {fmt(com.pagadasEnElMes)}</strong> en comisiones; de eso, <strong>{listaMeses(com.pagadasDeOtrosMeses, anio, fmt)}</strong> eran comisiones de antes, que ya están contadas en su mes.</> });
  }
  if (com.generadas > 0 && (com.pagadasDeOtrosMeses.length || com.pendientes > 0 || com.pagadasDespues.length)) {
    const partes = [];
    if (com.pagadasEnElMismoMes > 0) partes.push(`${fmt(com.pagadasEnElMismoMes)} pagadas en ${nombre}`);
    if (com.pagadasDespues.length) partes.push(`${listaMeses(com.pagadasDespues, anio, fmt, 'pagadas en')}`);
    if (com.pendientes > 0) partes.push(`${fmt(com.pendientes)} todavía pendientes`);
    lineas.push({ texto: <>Las comisiones <strong>generadas en {nombre}</strong> son <strong>{fmt(com.generadas)}</strong> ({com.equipos} equipos){partes.length ? `: ${partes.join(', ')}.` : '.'}</> });
  }
  if (caja.entregadoDeOtrosMeses.length) {
    lineas.push({ texto: <>Los técnicos <strong>entregaron {fmt(caja.entregadoEnElMes)}</strong> en {nombre}; de eso, <strong>{listaMeses(caja.entregadoDeOtrosMeses, anio, fmt)}</strong> era efectivo cobrado en meses anteriores.</> });
  }
  if (caja.enManosDelTecnico > 0 || caja.entregadoDespues.length) {
    const partes = [];
    if (caja.entregadoDespues.length) partes.push(`${listaMeses(caja.entregadoDespues, anio, fmt, 'se entregó en')}`);
    if (caja.enManosDelTecnico > 0) partes.push(`${fmt(caja.enManosDelTecnico)} sigue en manos de los técnicos`);
    lineas.push({ texto: <>Del efectivo cobrado en {nombre} ({fmt(caja.cobrado)}), {partes.join(' y ')}.</> });
  }
  if (com.sinRespaldo > 0) {
    lineas.push({ aviso: true, texto: <><strong>{fmt(com.sinRespaldo)}</strong> en comisiones están registradas pero el saldo del técnico nunca las reflejó, así que no se le pagaron. Conviene revisarlo.</> });
  }
  if (caja.sinRespaldo > 0) {
    lineas.push({ aviso: true, texto: <><strong>{fmt(caja.sinRespaldo)}</strong> de efectivo el saldo del técnico lo dio por entregado sin una entrega registrada que lo respalde.</> });
  }

  if (!lineas.length) return null;

  return (
    <Card sx={{ mb: 3, borderLeft: '5px solid', borderColor: 'info.main' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Lo generado en {nombre} y lo que se movió</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Este reporte cuenta cada peso en el mes en que se generó. Los pagos y entregas conservan su fecha real, así que en los movimientos del técnico pueden aparecer en otro mes.
        </Typography>
        <Stack spacing={1}>
          {lineas.map((l, i) => (
            <Typography key={i} variant="body2" sx={l.aviso ? { color: 'warning.dark' } : undefined}>• {l.texto}</Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

const FinancialReportPage: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await (apiService as any).api.get('/dashboard/financial-report', {
        params: { year: y, month: m }
      });
      setData(response.data?.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error cargando reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year, month);
  }, [year, month]);

  const fmt = (n: number) => `$${n.toLocaleString('es-DO')}`;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Reporte Financiero</Typography>
        <Typography variant="body1" color="text.secondary">
          Resumen de ingresos, comisiones y desglose por tipo de cliente
        </Typography>
      </Box>

      {/* Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Mes</InputLabel>
              <Select value={month} label="Mes" onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => (
                  <MenuItem key={i} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 100 }}>
              <InputLabel>Ano</InputLabel>
              <Select value={year} label="Ano" onChange={(e) => setYear(Number(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="h6" sx={{ ml: 2, color: 'primary.main', fontWeight: 600 }}>
              {MONTHS[month - 1]} {year}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Ingresos Totales"
                value={fmt(data.summary.totalRevenue)}
                subtitle={
                  `${data.summary.devicesInstalled ?? data.summary.installationCount} equipos instalados` +
                  (data.summary.devicesInstalledDealer ? ` (${data.summary.devicesInstalledDealer} de dealer)` : '') +
                  ` · ${data.summary.renewalCount} renovaciones` +
                  ((data.summary.invoicesRegisteredRevenue || 0) > 0
                    ? ` · Facturas en caja: ${fmt(data.summary.invoicesRegisteredRevenue)}`
                    : '')
                }
                icon={<TrendIcon sx={{ color: '#2E7D32', fontSize: 28 }} />}
                color="#2E7D32"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Gastos"
                value={fmt(data.summary.totalExpenses || 0)}
                subtitle="Registro de gastos (incl. publicidad)"
                icon={<ExpenseIcon sx={{ color: '#C62828', fontSize: 28 }} />}
                color="#C62828"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Comisiones Generadas"
                value={fmt(data.summary.commissionsEarned ?? data.summary.commissionsPaid)}
                subtitle={
                  `${data.conciliacion?.comisiones?.equipos ?? 0} equipos` +
                  (data.summary.commissionsPaid !== data.summary.commissionsEarned
                    ? ` · pagado en el mes: ${fmt(data.summary.commissionsPaid)}`
                    : '')
                }
                icon={<CommissionIcon sx={{ color: '#6A1B9A', fontSize: 28 }} />}
                color="#6A1B9A"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Ganancia Neta"
                value={fmt(data.summary.netProfit)}
                subtitle={data.summary.netProfit >= 0 ? 'En verde ✓' : 'En rojo — revisar'}
                icon={<BankIcon sx={{ color: data.summary.netProfit >= 0 ? '#2E7D32' : '#C62828', fontSize: 28 }} />}
                color={data.summary.netProfit >= 0 ? '#2E7D32' : '#C62828'}
              />
            </Grid>
          </Grid>

          {/* Resumen del neto: ingresos - comisiones - gastos */}
          <Card sx={{ mb: 3, borderLeft: '5px solid', borderColor: data.summary.netProfit >= 0 ? '#2E7D32' : '#C62828' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Cómo se forma el neto</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">Ingresos <strong style={{ color: '#2E7D32' }}>{fmt(data.summary.totalRevenue)}</strong></Typography>
                <Typography variant="body2">− Comisiones generadas <strong style={{ color: '#6A1B9A' }}>{fmt(data.summary.commissionsEarned ?? data.summary.commissionsPaid)}</strong></Typography>
                <Typography variant="body2">− Gastos <strong style={{ color: '#C62828' }}>{fmt(data.summary.totalExpenses || 0)}</strong></Typography>
                <Typography variant="body2">= Neto <strong style={{ color: data.summary.netProfit >= 0 ? '#2E7D32' : '#C62828' }}>{fmt(data.summary.netProfit)}</strong></Typography>
              </Stack>
            </CardContent>
          </Card>

          <ConciliacionCard c={data.conciliacion} mes={month} anio={year} fmt={fmt} />

          {/* Breakdown */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* By payment method */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Por Metodo de Pago</Typography>
                  <Stack spacing={2}>
                    {Object.entries(data.breakdown.byPaymentMethod).map(([method, amount]: [string, any]) => (
                      <Box key={method}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {method === 'cash' ? 'Efectivo' : method === 'bank_transfer' ? 'Transferencia' : 'Otro'}
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>{fmt(amount)}</Typography>
                        </Stack>
                        <Box sx={{ mt: 0.5, height: 6, borderRadius: 3, backgroundColor: '#E0E0E0' }}>
                          <Box sx={{
                            height: '100%', borderRadius: 3,
                            width: `${data.summary.totalRevenue > 0 ? (amount / data.summary.totalRevenue * 100) : 0}%`,
                            backgroundColor: method === 'cash' ? '#2E7D32' : method === 'bank_transfer' ? '#1565C0' : '#757575',
                          }} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Installations by client type */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Instalaciones por Tipo</Typography>
                  <Stack spacing={2}>
                    {Object.entries(data.breakdown.installationsByClientType).map(([type, amount]: [string, any]) => (
                      <Stack key={type} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {type === 'individual' ? 'Individual' : type === 'dealer' ? 'Dealer' : 'Empresa'}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>{fmt(amount)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Renewals by client type */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Renovaciones por Tipo</Typography>
                  <Stack spacing={2}>
                    {Object.entries(data.breakdown.renewalsByClientType).map(([type, amount]: [string, any]) => (
                      <Stack key={type} direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {type === 'individual' ? 'Individual' : type === 'dealer' ? 'Dealer' : 'Empresa'}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>{fmt(amount)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Ingresos por dealer (facturas en caja de clientes tipo dealer, del mes de la factura) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Ingresos por Dealer</Typography>
              {(data.breakdown.byDealer && data.breakdown.byDealer.length > 0) ? (
                <Stack spacing={1.5}>
                  {data.breakdown.byDealer.map((d: any) => (
                    <Box key={d.name}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">{d.name}</Typography>
                        <Typography variant="body1" fontWeight={600}>{fmt(d.total)}</Typography>
                      </Stack>
                      <Box sx={{ mt: 0.5, height: 6, borderRadius: 3, backgroundColor: '#E0E0E0' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 3,
                          width: `${data.breakdown.byDealer[0].total > 0 ? (d.total / data.breakdown.byDealer[0].total * 100) : 0}%`,
                          backgroundColor: '#6A1B9A',
                        }} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sin ingresos de dealers este mes. (Se cuentan las facturas de clientes tipo <em>dealer</em> puestas en caja, según el mes al que pertenece la factura.)
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Technician costs */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <TechIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Costos de Tecnicos
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Comisiones generadas en el mes</Typography>
                  <Typography variant="h5" fontWeight={600} color="error.main">
                    {fmt(data.summary.commissionsEarned ?? data.summary.commissionsPaid)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Comisiones pagadas en el mes</Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {fmt(data.summary.commissionsPaid)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Efectivo entregado por técnicos en el mes</Typography>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    {fmt(data.summary.cashCollected)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Ganancia neta</Typography>
                  <Typography variant="h5" fontWeight={600} color="primary.main">
                    {fmt(data.summary.netProfit)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      ) : null}
    </Box>
  );
};

export default FinancialReportPage;
